import { translateBackend } from '@assets/i18n/config';
import { AffiliationRelations } from '@interfaces/affiliationRelations';
import { ClientGroupMemberAccessTypes } from '@interfaces/clientGroup';
import type { CustomField } from '@interfaces/customField';
import SearchIcon from '@mui/icons-material/Search';
import { MenuItem } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import type {
  Segment,
  SegmentValue,
} from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SegmentedControl } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DraftHousehold, ExistingHousehold } from './interfaces';

export enum HouseholdActions {
  JOIN = 'joinHousehold',
  CREATE = 'createHousehold',
}

type SelectOption = {
  value?: string;
  label?: string;
};

export const HouseholdVisual = ({
  options,
  loading,
  continueFunc,
  householdAction,
  updateHouseholdAction,
  feeTierOptions,
  billingScheduleOptions,
  newHouseholdData,
  updateNewHouseholdData,
  householdOptions,
  joinHouseholdData,
  updateJoinHouseholdData,
  onHouseholdInputChange,
  activeCustomFields,
  derivedCustomFields,
}: {
  options: any;
  loading: boolean;
  continueFunc: any;
  householdAction?: SegmentValue;
  updateHouseholdAction: (value: SegmentValue) => void;
  feeTierOptions: SelectOption[];
  billingScheduleOptions: SelectOption[];
  newHouseholdData?: DraftHousehold;
  updateNewHouseholdData: (value: DraftHousehold) => void;
  householdOptions: SelectOption[];
  joinHouseholdData?: ExistingHousehold;
  updateJoinHouseholdData: (value: ExistingHousehold) => void;
  onHouseholdInputChange?: (value: string) => void;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
}) => {
  const { t } = useTranslation([
    'affiliationTypes',
    'household',
    'workflowCompletions',
  ]);
  const [focused, setFocused] = useState<string[]>([]);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields } = useCustomFieldRenderer({
    customOptions: customOptionSelected,
    customFieldValues: newHouseholdData?.customFields,
  });

  useEffect(() => {
    const customOptions: any[] = [];
    Object.keys(options).forEach((key: string) => {
      if (
        options[key]?.customField &&
        options[key].enabled &&
        activeCustomFields?.includes(options[key]?.customField?.key)
      ) {
        const cfKey = options[key]?.customField?.key;
        const freshField = derivedCustomFields?.find((cf) => cf.key === cfKey);
        if (freshField) {
          customOptions.push({
            ...options[key],
            customField: {
              ...options[key].customField,
              selectOptions:
                freshField.selectOptions ??
                options[key].customField.selectOptions,
              translatedName:
                freshField.translatedName ??
                options[key].customField.translatedName,
              translatedDescription:
                freshField.translatedDescription ??
                options[key].customField.translatedDescription,
            },
          });
        } else {
          customOptions.push(options[key]);
        }
      }
    });
    setCustomOptionSelected(customOptions);
  }, [options, activeCustomFields, derivedCustomFields]);

  const segments: Segment[] = [
    {
      value: HouseholdActions.CREATE,
      label: t('workflowCompletions:household.create'),
    },
    {
      value: HouseholdActions.JOIN,
      label: t('workflowCompletions:household.join'),
    },
  ];

  const createHouseholdForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('workflowCompletions:household.name')}
          onChange={(e: any) =>
            updateNewHouseholdData({
              ...newHouseholdData,
              name: e.target.value,
            })
          }
        />
      </Grid>
      {options?.allowFeeGrid && (
        <Grid item xs>
          <SelectField
            fullWidth
            label={t('workflowCompletions:household.feeGrid')}
            onChange={(e: any) =>
              updateNewHouseholdData({
                ...newHouseholdData,
                feeTierId: e.target.value,
              })
            }
          >
            {feeTierOptions.map((feeTier: SelectOption) => (
              <MenuItem value={feeTier.value}>{feeTier.label}</MenuItem>
            ))}
          </SelectField>
        </Grid>
      )}
      {options?.allowBillingSchedule && (
        <Grid item xs>
          <SelectField
            fullWidth
            label={t('workflowCompletions:household.billingSchedule')}
            onChange={(e: any) =>
              updateNewHouseholdData({
                ...newHouseholdData,
                billingScheduleId: e.target.value,
              })
            }
          >
            {billingScheduleOptions.map((billingSchedule: SelectOption) => (
              <MenuItem value={billingSchedule.value}>
                {billingSchedule.label}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
      )}
      <Grid item xs={12}>
        {renderCustomFields({
          customData: newHouseholdData,
          update: updateNewHouseholdData,
          focused,
          setFocused,
          loading,
        })}
      </Grid>
    </Grid>
  );

  const householdRelationshipOptions = Object.values(AffiliationRelations).map(
    (relation, index) => (
      <MenuItem key={index} value={relation}>
        {t(`affiliationTypes:affiliationRelationship.${relation}`)}
      </MenuItem>
    ),
  );
  const accessTypeOptions = Object.values(ClientGroupMemberAccessTypes).map(
    (accessType, index) => (
      <MenuItem key={index} value={accessType}>
        {t(`household:accessTypes.${accessType}`)}
      </MenuItem>
    ),
  );

  const joinHouseholdForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Autocomplete
          label={t('workflowCompletions:household.name')}
          value={joinHouseholdData?.clientGroup}
          options={householdOptions}
          onChange={(_e: any, value: any) =>
            updateJoinHouseholdData({
              ...joinHouseholdData,
              clientGroup: value,
            })
          }
          onInputChange={(_e: React.SyntheticEvent, value: string) =>
            onHouseholdInputChange?.(value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: undefined,
          }}
          fullWidth
        ></Autocomplete>
      </Grid>
      <Grid item xs={12}>
        <SelectField
          fullWidth
          label={t(
            'workflowCompletions:household.relationshipToPrimaryContact',
          )}
          value={joinHouseholdData?.relationshipType}
          onChange={(e: any) =>
            updateJoinHouseholdData({
              ...joinHouseholdData,
              relationshipType: e.target.value,
            })
          }
        >
          {householdRelationshipOptions}
        </SelectField>
      </Grid>
      <Grid item xs={12}>
        <SelectField
          fullWidth
          label={t('workflowCompletions:household.permissions')}
          value={joinHouseholdData?.accessType}
          onChange={(e: any) =>
            updateJoinHouseholdData({
              ...joinHouseholdData,
              accessType: e.target.value,
            })
          }
        >
          {accessTypeOptions}
        </SelectField>
      </Grid>
    </Grid>
  );

  return (
    <WorkflowCompletionForm
      onSubmit={continueFunc}
      testId='household-form'
      submitButtonProps={{
        label:
          householdAction === HouseholdActions.CREATE
            ? t('workflowCompletions:household.createSubmit')
            : t('workflowCompletions:household.joinSubmit'),
        disabled: loading,
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      <SegmentedControl
        sx={{ mb: 4 }}
        value={householdAction}
        segments={segments}
        exclusive={true}
        fullWidth
        enforceActive
        onChange={updateHouseholdAction}
      />
      {householdAction === HouseholdActions.CREATE && createHouseholdForm}
      {householdAction === HouseholdActions.JOIN && joinHouseholdForm}
    </WorkflowCompletionForm>
  );
};
